// Modules
import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { withMapsProvider } from './contexts/MapsContext';
import { renderRoutes } from 'routes/renderRoutes';
import { adminRoutes } from 'routes/AdminRoutes';
import { customerRoutes } from 'routes/CustomerRoutes';
import { partnerRoutes } from 'routes/PartnerRoutes';
import { featureConfig } from 'config/features';
import { useResponsiveListener } from 'utils/mobile-utils';

// UI
import MainLayout from 'components/layout/MainLayout';
import { CustomerLayout } from 'components/layout/CustomerLayout';
import { PartnerLayout } from 'components/layout/PartnerLayout';
import Notification from 'components/Notification';
import { Spinner } from 'components/common/Spinner';

// Lazy loading wrapper with Suspense fallback
const LazyLoad = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={
    <div className="flex items-center justify-center w-full h-screen">
      <Spinner size="lg" />
    </div>
  }>
    {children}
  </Suspense>
);

// Lazy-loaded Pages
const Login = lazy(() => import('pages/Login'));
const Onboarding = lazy(() => import('pages/Onboarding'));
const ForgotPassword = lazy(() => import('pages/Login/ForgotPassword'));
const ResetPassword = lazy(() => import('pages/Login/ResetPassword'));
const EmailVerification = lazy(() => import('pages/EmailVerification'));
const SubscriptionSuccess = lazy(() => import('pages/Subscription/SubscriptionSuccess'));
const SubscriptionFail = lazy(() => import('pages/Subscription/SubscriptionFail'));
const Forbidden = lazy(() => import('pages/Forbidden'));
// CustomerLead uses location picker, so it needs MapsProvider
const CustomerLead = lazy(() => 
  import('pages/Leads/CustomerLead').then(m => ({ default: withMapsProvider(m.default) }))
);

// Public Quote Pages (no auth required)
const PublicQuoteView = lazy(() => import('pages/Quote/PublicQuoteView'));
const QuoteAccepted = lazy(() => import('pages/Quote/QuoteAccepted'));
const QuoteRejected = lazy(() => import('pages/Quote/QuoteRejected'));
const QuoteError = lazy(() => import('pages/Quote/QuoteError'));

// Customer Pages
const CustomerLogin = lazy(() => import('pages/Customer/CustomerLogin'));

// Partner Pages
const PartnerLogin = lazy(() => import('pages/Partners/PartnerLogin'));
const PartnerAcceptInvite = lazy(() => import('pages/Partners/PartnerAcceptInvite'));

const App = () => {
  useResponsiveListener();

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/login" element={<LazyLoad><Login /></LazyLoad>} />
        <Route path="/onboarding" element={<LazyLoad><Onboarding /></LazyLoad>} />
        <Route path="/subscription/success" element={<LazyLoad><SubscriptionSuccess /></LazyLoad>} />
        <Route path="/subscription/failed" element={<LazyLoad><SubscriptionFail /></LazyLoad>} />
        <Route path="/customers/login" element={<LazyLoad><CustomerLogin /></LazyLoad>} />
        <Route path="/verify-email/:token" element={<LazyLoad><EmailVerification /></LazyLoad>} />

        {/* CustomerLead has MapsProvider via withMapsProvider HOC */}
        <Route path="leads/forms/embed/:id" element={<LazyLoad><CustomerLead /></LazyLoad>} />

        {/* Public Quote Routes (no auth required) */}
        <Route path="/quote/view/:token" element={<LazyLoad><PublicQuoteView /></LazyLoad>} />
        <Route path="/quote/accepted" element={<LazyLoad><QuoteAccepted /></LazyLoad>} />
        <Route path="/quote/rejected" element={<LazyLoad><QuoteRejected /></LazyLoad>} />
        <Route path="/quote/error" element={<LazyLoad><QuoteError /></LazyLoad>} />

        {/* Customer routes with dedicated layout */}
        <Route path="/customers" element={<CustomerLayout />}>
          {renderRoutes(customerRoutes)}
        </Route>

        {/* Partner routes with dedicated layout */}
        {featureConfig.partners && (
          <>
            <Route path="/partners/login" element={<LazyLoad><PartnerLogin /></LazyLoad>} />
            <Route path="/partners/accept-invite/:token" element={<LazyLoad><PartnerAcceptInvite /></LazyLoad>} />
            <Route path="/partners" element={<PartnerLayout />}>
              {renderRoutes(partnerRoutes)}
            </Route>
          </>
        )}

        {/* Admin routes with main layout */}
        <Route path="/" element={<MainLayout />}>
          {renderRoutes(adminRoutes)}
          <Route path="forbidden" element={<LazyLoad><Forbidden /></LazyLoad>} />
        </Route>

        <Route path="/forgot-password" element={<LazyLoad><ForgotPassword /></LazyLoad>} />
        <Route path="/reset-password/:token" element={<LazyLoad><ResetPassword /></LazyLoad>} />
      </Routes>

      <Notification />
    </Router>
  );
};

export default App;
